import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PackageOpen, X } from "lucide-react";
import { playRequestChime } from "@/lib/chime";
import { fmtQty } from "@/lib/stockUnits";

const qtyOf = (a) => (a.source === "ingredient" && a.unit ? fmtQty(a.remaining, a.unit) : a.remaining);
const thOf = (a) => (a.source === "ingredient" && a.unit ? fmtQty(a.threshold, a.unit) : a.threshold);

// Bandeau partagé POS / Manager : alertes « stock faible » et « épuisé » des plats du jour (source commune stock_alerts).
export function useStockAlerts(slug, soundRef) {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => { if (slug) api.getStockAlerts(slug).then(setAlerts).catch(() => {}); }, [slug]);
  const handlers = {
    stock_alert: (a) => {
      setAlerts((p) => [a, ...p.filter((x) => !(x.source === a.source && x.ref_id === a.ref_id))]);
      if (soundRef?.current) playRequestChime("stock");
      const msg = a.level === "out" ? `Épuisé : ${a.name}` : `Plus que ${qtyOf(a)} × ${a.name}`;
      toast(a.level === "out" ? `⛔ ${msg}` : `⚠️ ${msg}`, { id: `stock-${a.ref_id}`, duration: 12000, description: a.source === "ingredient" ? "Stock ingrédient — à réapprovisionner" : "Plat du jour — à annoncer aux tables" });
    },
    stock_alert_cleared: (a) => { setAlerts((p) => p.filter((x) => !(x.source === a.source && x.ref_id === a.ref_id))); toast.dismiss(`stock-${a.ref_id}`); },
    stock_alert_updated: (a) => {
      setAlerts((p) => p.map((x) => (x.source === a.source && x.ref_id === a.ref_id ? { ...x, remaining: a.remaining } : x)));
      toast.dismiss(`stock-${a.ref_id}`);
    },
    menu_updated: () => api.getStockAlerts(slug).then(setAlerts).catch(() => {}),
  };
  return { alerts, handlers, dismiss: (a) => setAlerts((p) => p.filter((x) => x.id !== a.id)) };
}

export function StockAlertBanner({ alerts, onDismiss }) {
  if (!alerts?.length) return null;
  return (
    <div data-testid="stock-alert-banner" className="px-4 pt-2 space-y-1.5">
      {alerts.map((a) => (
        <div key={a.id} data-testid={`stock-alert-${a.ref_id}`} className={`flex items-center gap-2.5 rounded-xl px-3 py-2 border text-sm ${a.level === "out" ? "bg-red-950/60 border-red-500/50 text-red-100" : "bg-amber-950/50 border-amber-500/50 text-amber-100"}`}>
          <PackageOpen className="w-4 h-4 shrink-0" />
          <span className="flex-1 min-w-0 truncate">
            <b>{a.level === "out" ? "Épuisé" : `Plus que ${qtyOf(a)}`}</b> × {a.name} <span className="opacity-70 text-xs">· {a.source === "ingredient" ? "ingrédient" : "plat du jour"} (seuil {thOf(a)})</span>
          </span>
          <button data-testid={`stock-alert-dismiss-${a.ref_id}`} onClick={() => onDismiss(a)} className="opacity-70 hover:opacity-100 p-0.5" aria-label="Masquer"><X className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}
